import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';
import { generatePath, useNavigate } from 'react-router-dom';
import { faEyeSlash } from '@fortawesome/pro-regular-svg-icons/faEyeSlash';
import { faList } from '@fortawesome/pro-regular-svg-icons/faList';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons/faSparkles';
import { faCameraSlash } from '@fortawesome/pro-regular-svg-icons/faCameraSlash';
import { faPartyHorn } from '@fortawesome/pro-regular-svg-icons/faPartyHorn';
import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons/faCircleExclamation';

import { colors } from 'us-react-components/dist/constants/Styles';
import { NotificationBanner, NotificationBannerProps } from 'us-react-components/dist/NotificationBanner/NotificationBanner';
import { HiddenReasonType, Sitter } from 'us-web-services';
import { useDependencies } from '../../dependencies';
import { Routes } from '../../Routes';

interface Props {
  hasBookings: boolean;
}

const scrollTo = (elementId: string): void => {
  const element = document.getElementById(elementId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

const HiddenReasonBanner = ({ hasBookings }: Props): JSX.Element | null => {
  const { services: { tracker }, stores: { sitter } } = useDependencies();
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id } = sitter.sitter;

  const BANNERS: Record<HiddenReasonType, NotificationBannerProps> = {
    [HiddenReasonType.DECISION]: {
      title: 'components.hiddenReasonBanner.decision.heading',
      body: 'components.hiddenReasonBanner.decision.body',
      color: colors.gray[400],
      icon: faEyeSlash,
    },
    [HiddenReasonType.PENDING_APPROVAL]: {
      title: 'components.hiddenReasonBanner.pendingApproval.heading',
      body: 'components.hiddenReasonBanner.pendingApproval.body',
      buttonTitle: 'components.hiddenReasonBanner.viewApprovalChecklist',
      color: colors.kiwi[700],
      icon: faList,
      onClick: () => {
        const trackingProps = {
          elementId: 'hidden-pending-approval',
          elementText: t('components.hiddenReasonBanner.viewApprovalChecklist'),
        };

        tracker.track('clicked-button', trackingProps);
        scrollTo('caregiver-approval-checklist');
      },
    },
    [HiddenReasonType.READY_FOR_REVIEW]: {
      title: 'components.hiddenReasonBanner.readyForReview.heading',
      body: 'components.hiddenReasonBanner.readyForReview.body',
      color: colors.ocean.DEFAULT,
      icon: faSparkles,
    },
    [HiddenReasonType.SOLICITOR]: {
      title: 'components.hiddenReasonBanner.decision.heading',
      body: 'components.hiddenReasonBanner.decision.body',
      color: colors.gray[400],
      icon: faEyeSlash,
    },
    [HiddenReasonType.PHOTO]: {
      title: 'components.hiddenReasonBanner.hiddenForPhoto.heading',
      body: 'components.hiddenReasonBanner.hiddenForPhoto.body',
      color: colors.tangerine.DEFAULT,
      icon: faCameraSlash,
      buttonTitle: 'components.hiddenReasonBanner.hiddenForPhoto.buttonTitle',
      onClick: () => {
        const trackingProps = {
          elementId: 'hidden-for-photo-button',
          elementText: t('components.hiddenReasonBanner.hiddenForPhoto.buttonTitle'),
        };

        tracker.track('clicked-button', trackingProps);
        navigate(generatePath(Routes.sitter.profile, { id }));
      }
    },
    [HiddenReasonType.INCOMPLETE_PROFILE]: {
      title: 'components.hiddenReasonBanner.hiddenForIncompleteProfile.heading',
      body: 'components.hiddenReasonBanner.hiddenForIncompleteProfile.body',
      buttonTitle: 'components.hiddenReasonBanner.viewApprovalChecklist',
      color: colors.tangerine.DEFAULT,
      icon: faCircleExclamation,
      onClick: () => {
        const trackingProps = {
          elementId: 'hidden-incomplete-profile',
          elementText: t('components.hiddenReasonBanner.viewApprovalChecklist'),
        };

        tracker.track('clicked-button', trackingProps);
        scrollTo('caregiver-approval-checklist');
      },
    },
    [HiddenReasonType.UNHIDDEN]: {
      title: 'components.hiddenReasonBanner.unhiddenNoBookings.heading',
      body: 'components.hiddenReasonBanner.unhiddenNoBookings.body',
      color: colors.ocean.DEFAULT,
      icon: faPartyHorn,
    },
  };

  const getBannerProps = (sitterObject?: Sitter): NotificationBannerProps | undefined => {
    if (sitterObject === undefined) {
      return undefined;
    }

    const reason = sitterObject?.hiddenReason || HiddenReasonType.UNHIDDEN;

    if (reason === HiddenReasonType.UNHIDDEN && hasBookings) {
      return undefined;
    }

    return BANNERS[reason as HiddenReasonType];
  }

  const bannerProps = getBannerProps(sitter.sitter);

  return bannerProps
    ? <NotificationBanner
        icon={bannerProps.icon}
        title={t(bannerProps.title)}
        body={t(bannerProps.body as string)}
        color={bannerProps.color}
        buttonTitle={bannerProps.buttonTitle && t(bannerProps.buttonTitle)}
        onClick={bannerProps.onClick}
      />
    : null;
};

export { HiddenReasonBanner };
